<script>
  import upperFirst from "lodash/upperFirst";
  import get from "lodash/get";
  import { createEventDispatcher } from "svelte";

  import TextInput from "../sidebar/TextInput.svelte";
  import MultiTextInput from "../sidebar/MultiTextInput.svelte";
  import SelectInput from "../sidebar/SelectInput.svelte";
  import BooleanInput from "../sidebar/BooleanInput.svelte";
  import EllipsisIcon from "@local/assets/icons/ellipsis.svg";
  import locationsByDocument from "#src/extensions/locations-by-document.js";
  import HiddenColumns from "#lib/settings/HiddenColumns.svelte";

  import { profile } from "#src/stores/auth.js";

  export let group;
  export let items;
  export let collections;
  export let documentNotes;
  export let settings;
  export let customColumns;
  export let standardColumns;
  export let customColColumns;
  export let cloneable = false;

  let showActions = false;
  let tab = "setup";

  const dispatch = createEventDispatcher();

  $: capabilities = $profile?.profile_capabilities?.capabilities || {};
  $: filterOptions = makeFilterOptions(customColumns);
  $: colFilterOptions = makeFilterOptions(customColColumns);
  $: noteFilterOptions = makeFilterOptions([]);
  $: docOptions = $group.documents.order.map((id) => {
    const doc = $group.documents[id];
    return { label: doc.name, value: doc.id };
  });
  $: linkedDocOptions = $group.documents.order
    .map((id) => $group.documents[id])
    .filter((doc) => doc.type === "imported")
    .map((doc) => ({ label: doc.name, value: doc.id }));
  $: sheetSectionOptions = getSheetSections($group.documents[settings.count_document_id]);
  $: collSSOptions = getSheetSections($group.documents[settings.collection_count_document_id]);
  $: locCategoryOptions = getLocCategoryOptions($group.documents[settings.count_document_id]);
  $: itemFilterOptions = getFilterValues(settings?.item_filter, items);
  $: collFilterOptions = getFilterValues(settings?.collection_filter, collections);
  $: noteFilterValues = getFilterValues(settings?.note_filter, documentNotes);

  function getLinkedDocument(grp, id) {
    return grp.documents.order.find((id) => {
      const doc = grp.documents[id];
      return doc.linked_report_id === settings.id;
    });
  }

  function getFilterValues(col, shapes) {
    if (!col || !shapes) return [];

    const vals = shapes.reduce((acc, item) => {
      const v = get(item, col.field);
      if (v != null && v !== "") {
        acc[v] = true;
      }
      return acc;
    }, {});

    return Object.keys(vals).map((v) => ({
      label: v,
      value: v,
    }));
  }

  function getSheetSections(doc) {
    if (!doc) return [];

    const presentPages = doc.data.pages.order.reduce((acc, pid) => {
      const page = doc.data.pages[pid];
      if (page.section) {
        acc[page.section] = true;
      }

      return acc;
    }, {});

    return Object.keys(presentPages).map((section) => ({
      label: section,
      value: section,
    }));
  }

  function getLocCategoryOptions(doc) {
    if (!doc) return [];

    const docLocations = locationsByDocument(doc);
    const locations = docLocations[doc.id] || [];

    const presentCats = locations.reduce((acc, loc) => {
      if (loc.category) {
        acc[loc.category] = true;
      }
      return acc;
    }, {});

    return Object.keys(presentCats).map((cat) => ({
      label: cat,
      value: cat,
    }));
  }

  function updateCountMethod(method) {
    if (method !== "direct_entry") {
      const first = linkedDocOptions[0]?.value || null;

      dispatch("update-setting", {
        diff: {
          count_method: method,
          count_document_id: first,
          count_sheet_section: [],
        },
      });
    } else {
      dispatch("update-setting", { prop: "count_method", value: method });
    }
  }

  function updateCollectionCountMethod(method) {
    if (method !== "direct_entry") {
      const first = linkedDocOptions[0]?.value || null;

      dispatch("update-setting", {
        diff: {
          collection_count_method: method,
          collection_count_document_id: first,
          collection_count_sheet_section: [],
        },
      });
    } else {
      dispatch("update-setting", { prop: "collection_count_method", value: method });
    }
  }

  function makeFilterOptions(custom) {
    const sc = [
      { label: "None", value: undefined },
      { label: "Description", value: "description" },
      { label: "Scope", value: "scope" },
    ].map((col) => {
      if (standardColumns[col.value]?.override) {
        col.label = standardColumns[col.value].override;
      }
      return col;
    });

    return sc.concat(
      custom.map((c) => ({
        label: c.name,
        value: `custom_column_data.${c.id}`,
      })),
    );
  }

  function makeSortOptions(customColumns) {
    const sc = [
      { label: "Default", value: null },
      { label: "Created at", value: "created_at" },
      { label: "Mark", value: "mark" },
      { label: "Width", value: "width" },
      { label: "Height", value: "height" },
      { label: "Quantity", value: "quantity" },
      { label: "Description", value: "description" },
    ].map((col) => {
      if (standardColumns[col.value]?.override) {
        col.label = standardColumns[col.value].override;
      }
      return col;
    });

    return sc.concat(
      customColumns.map((c) => ({
        label: c.name,
        value: `custom_column_data.${c.id}`,
      })),
    );
  }

  function updateSetting(prop, value) {
    dispatch("update-setting", { prop, value });
  }

  function updateFilterField(e, field = "item_filter") {
    const { value } = e.detail;

    let filter;
    if (!value) {
      filter = null;
    } else {
      filter = {
        field: value,
        operator: "is_one_of",
        value: "",
      };
    }

    updateSetting(field, filter);
  }

  function updateFilterOperator(e, field = "item_filter") {
    const { value } = e.detail;
    const filter = {
      ...settings[field],
      operator: value,
      value: null,
    };

    updateSetting(field, filter);
  }

  function toggleActionMenu() {
    showActions = !showActions;
  }

  function cloneReport() {
    showActions = false;
    dispatch("clone-report", { settings });
  }

  function updateFilterValue(e, field = "item_filter") {
    const { value } = e.detail;
    const filter = {
      ...settings[field],
      value: value,
    };

    updateSetting(field, filter);
  }
</script>

<div class="mb-4 flex justify-between border-b">
  <div class="flex items-center gap-4 setting-tabs">
    <button class:selected={tab === "setup"} on:click={() => (tab = "setup")}> Setup </button>
    {#if settings.group_by === "type" || settings.group_by === "opening"}
      <button class:selected={tab === "counting"} on:click={() => (tab = "counting")}> Counting </button>
      <button class:selected={tab === "visibility"} on:click={() => (tab = "visibility")}>
        Visibility
      </button>
    {/if}
  </div>
  {#if cloneable}
    <div class="flex-none relative w-8 flex justify-end">
      <button class="p-1 rounded hover:bg-gray-200" on:click={toggleActionMenu}>
        <EllipsisIcon />
      </button>
      {#if showActions}
        <div class="absolute border bg-white rounded-lg shadow right-0 top-6 z-30 p-1 text-right">
          <button class="hover:bg-gray-200 py-1 px-2 rounded whitespace-nowrap" on:click={cloneReport}>
            Clone Report
          </button>
        </div>
      {/if}
    </div>
  {/if}
</div>
{#if tab === "setup"}
  <div>
    <div class="flex items-center justify-between relative">
      <div class="flex-none w-56">Report Name</div>
      <div class="grow">
        <TextInput
          value={settings.report_name}
          label={null}
          on:input={(e) => updateSetting("report_name", e.detail.value)} />
      </div>
    </div>
    {#if capabilities.edit_report_group_by}
      <div class="flex items-center justify-between">
        <div class="flex-none w-56">Report Type</div>
        <div class="grow">
          <SelectInput
            options={[
              { label: "Grouped by Type", value: "type" },
              { label: "Grouped by Opening", value: "opening" },
              { label: "Note Schedule", value: "notes" },
              { label: "Linear Length Schedule", value: "linear" },
            ]}
            value={settings.group_by}
            label={null}
            on:input={(e) => updateSetting("group_by", e.detail.value)} />
        </div>
      </div>
    {/if}
    {#if settings.group_by === "notes"}
      <div class="rounded-lg border border-gray-300 p-3 my-2 bg-gray-100 space-y-2">
        <div class="font-bold">Filter Notes</div>
        <div class="flex items-center justify-between">
          <div class="flex-none" style="width: 13.25rem;">Filter By</div>
          <div class="grow">
            <SelectInput
              outerBorder={false}
              showCaret
              options={noteFilterOptions}
              value={settings.note_filter?.field}
              label={null}
              on:input={(e) => updateFilterField(e, "note_filter")} />
          </div>
        </div>
        {#if settings.note_filter}
          <div class="flex items-center justify-between gap-2">
            <div class="flex-none pl-32" style="width: 12.75rem;">
              <SelectInput
                outerBorder={false}
                showCaret
                disabled={!settings.note_filter}
                placeholder="&nbsp;"
                options={[
                  { label: "Is", value: "is_one_of" },
                  { label: "Is Not", value: "is_not_one_of" },
                ]}
                value={settings.note_filter?.operator}
                label={null}
                on:input={(e) => updateFilterOperator(e, "note_filter")} />
            </div>
            <div class="grow">
              <MultiTextInput
                outerBorder={false}
                label={null}
                placeholder="Enter Filter Value(s)"
                value={settings.note_filter?.value}
                options={noteFilterValues}
                on:input={(e) => updateFilterValue(e, "note_filter")} />
            </div>
          </div>
        {/if}
      </div>
    {:else if settings.group_by === "linear"}
      <div class="rounded-lg border border-gray-300 p-3 my-2 bg-gray-100 space-y-2">
        <div class="font-bold">Filter Openings</div>
        <div class="flex items-center justify-between">
          <div class="flex-none" style="width: 13.25rem;">Filter By</div>
          <div class="grow">
            <SelectInput
              outerBorder={false}
              showCaret
              options={colFilterOptions}
              value={settings.collection_filter?.field}
              label={null}
              on:input={(e) => updateFilterField(e, "collection_filter")} />
          </div>
        </div>
        {#if settings.collection_filter}
          <div class="flex items-center justify-between gap-2">
            <div class="flex-none pl-32" style="width: 12.75rem;">
              <SelectInput
                outerBorder={false}
                showCaret
                disabled={!settings.collection_filter}
                placeholder="&nbsp;"
                options={[
                  { label: "Is", value: "is_one_of" },
                  { label: "Is Not", value: "is_not_one_of" },
                ]}
                value={settings.collection_filter?.operator}
                label={null}
                on:input={(e) => updateFilterOperator(e, "collection_filter")} />
            </div>
            <div class="grow">
              <MultiTextInput
                outerBorder={false}
                label={null}
                placeholder="Enter Filter Value(s)"
                value={settings.collection_filter?.value}
                options={collFilterOptions}
                on:input={(e) => updateFilterValue(e, "collection_filter")} />
            </div>
          </div>
        {/if}
      </div>
    {:else}
      <div class="rounded-lg border border-gray-300 p-3 my-2 bg-gray-100 space-y-2">
        <div class="font-bold">Filter Items</div>
        <div class="flex items-center justify-between">
          <div class="flex-none" style="width: 13.25rem;">Filter By</div>
          <div class="grow">
            <SelectInput
              outerBorder={false}
              showCaret
              options={filterOptions}
              value={settings.item_filter?.field}
              label={null}
              on:input={updateFilterField} />
          </div>
        </div>
        {#if settings.item_filter}
          <div class="flex items-center justify-between gap-2">
            <div class="flex-none pl-32" style="width: 12.75rem;">
              <SelectInput
                outerBorder={false}
                showCaret
                disabled={!settings.item_filter}
                placeholder="&nbsp;"
                options={[
                  { label: "Is", value: "is_one_of" },
                  { label: "Is Not", value: "is_not_one_of" },
                ]}
                value={settings.item_filter?.operator}
                label={null}
                on:input={updateFilterOperator} />
            </div>
            <div class="grow">
              <MultiTextInput
                outerBorder={false}
                label={null}
                placeholder="Enter Filter Value(s)"
                value={settings.item_filter?.value}
                options={itemFilterOptions}
                on:input={updateFilterValue} />
            </div>
          </div>
        {/if}
      </div>
    {/if}
    {#if settings.group_by === "opening"}
      <div class="rounded-lg border border-gray-300 p-3 my-2 bg-gray-100 space-y-2">
        <div class="font-bold">Filter Openings</div>
        <div class="flex items-center justify-between">
          <div class="flex-none" style="width: 13.25rem;">Filter By</div>
          <div class="grow">
            <SelectInput
              outerBorder={false}
              showCaret
              options={colFilterOptions}
              value={settings.collection_filter?.field}
              label={null}
              on:input={(e) => updateFilterField(e, "collection_filter")} />
          </div>
        </div>
        {#if settings.collection_filter}
          <div class="flex items-center justify-between gap-2">
            <div class="flex-none pl-32" style="width: 12.75rem;">
              <SelectInput
                outerBorder={false}
                showCaret
                disabled={!settings.collection_filter}
                placeholder="&nbsp;"
                options={[
                  { label: "Is", value: "is_one_of" },
                  { label: "Is Not", value: "is_not_one_of" },
                ]}
                value={settings.collection_filter?.operator}
                label={null}
                on:input={(e) => updateFilterOperator(e, "collection_filter")} />
            </div>
            <div class="grow">
              <MultiTextInput
                outerBorder={false}
                label={null}
                placeholder="Enter Filter Value(s)"
                value={settings.collection_filter?.value}
                options={collFilterOptions}
                on:input={(e) => updateFilterValue(e, "collection_filter")} />
            </div>
          </div>
        {/if}
      </div>
    {/if}
    {#if settings.group_by === "type" || settings.group_by === "opening"}
      <div class="flex items-center justify-between">
        <div class="flex-none w-56">Default View (Web)</div>
        <div class="grow">
          <SelectInput
            options={[
              { label: `Show ${upperFirst(settings.group_by)} Details`, value: -1 },
              { label: `Show ${upperFirst(settings.group_by)} Details and Items`, value: 1 },
              { label: `Show ${upperFirst(settings.group_by)} Name Only`, value: 0 },
            ]}
            value={settings.default_type_view}
            label={null}
            on:input={(e) => updateSetting("default_type_view", e.detail.value)} />
        </div>
      </div>
    {/if}
    <div class="flex items-center justify-between">
      <div class="flex-none w-56">Link Destination</div>
      <div class="grow">
        <SelectInput
          options={[
            settings.group_by === "notes"
              ? { label: "First Occurence", value: null }
              : { label: "Detail Page", value: null },
            ...docOptions,
          ]}
          value={settings.link_destination}
          label={null}
          on:input={(e) => updateSetting("link_destination", e.detail.value)} />
      </div>
    </div>
    {#if settings.group_by === "notes"}
      <div class="flex items-center justify-between">
        <div class="flex-none w-56">Show Note Body in Table</div>
        <div class="grow">
          <BooleanInput
            value={settings.show_note_body}
            label={null}
            on:input={(e) => updateSetting("show_note_body", e.detail.value)} />
        </div>
      </div>
    {/if}
    {#if settings.group_by !== "notes"}
      <div class="flex items-center justify-between">
        <div class="flex-none w-56">Final Callout</div>
        <div class="grow">
          <SelectInput
            options={[
              { label: "Total Area", value: "area" },
              {
                label: `${settings.group_by === "opening" ? "Opening" : "Type"} Quantity`,
                value: "quantity",
              },
              { label: "Total Units", value: "units" },
              { label: "None", value: "none" },
            ]}
            value={settings.category_callout}
            label={null}
            on:input={(e) => updateSetting("category_callout", e.detail.value)} />
        </div>
      </div>
    {/if}
  </div>
{:else if tab === "counting"}
  <div>
    <div class="rounded-lg border border-gray-300 p-3 my-2 bg-gray-100 space-y-2">
      <div class="font-bold">Item Counts</div>
      <div class="flex items-center justify-between">
        <div class="flex-none" style="width: 13.25rem;">Count Method</div>
        <div class="grow">
          <SelectInput
            outerBorder={false}
            options={[
              { label: "Direct Entry (Item Qty)", value: "direct_entry" },
              { label: "By Sheet Type", value: "sheet_section" },
              { label: "By Mark Category", value: "location_category" },
              { label: "By Count Checkbox", value: "count_checkbox" },
            ]}
            value={settings.count_method}
            label={null}
            on:input={(e) => updateCountMethod(e.detail.value)} />
        </div>
      </div>
      {#if settings.count_method !== "direct_entry"}
        <div class="flex items-center justify-between">
          <div class="flex-none" style="width: 13.25rem;">Document</div>
          <div class="grow">
            <SelectInput
              outerBorder={false}
              label={null}
              options={linkedDocOptions}
              value={settings.count_document_id}
              placeholder="Select Document"
              on:input={(e) => updateSetting("count_document_id", e.detail.value)} />
          </div>
        </div>
        {#if settings.count_method === "sheet_section" && settings.count_document_id !== null}
          <div class="flex items-center justify-between">
            <div class="flex-none" style="width: 13.25rem;">Sheet Type</div>
            <div class="grow">
              <MultiTextInput
                outerBorder={false}
                label={null}
                placeholder="Enter Sheet Type"
                value={settings.count_sheet_section}
                options={sheetSectionOptions}
                on:input={(e) => updateSetting("count_sheet_section", e.detail.value)} />
            </div>
          </div>
        {:else if settings.count_method === "location_category"}
          <div class="flex items-center justify-between">
            <div class="flex-none" style="width: 13.25rem;">Mark Category</div>
            <div class="grow">
              <MultiTextInput
                outerBorder={false}
                label={null}
                placeholder="Enter Mark Category"
                value={settings.count_location_category}
                options={locCategoryOptions}
                on:input={(e) => updateSetting("count_location_category", e.detail.value)} />
            </div>
          </div>
        {/if}
      {/if}
    </div>
    <div class="rounded-lg border border-gray-300 p-3 my-2 bg-gray-100 space-y-2">
      <div class="font-bold">Opening Counts</div>
      <div class="flex items-center justify-between">
        <div class="flex-none" style="width: 13.25rem;">Count Method</div>
        <div class="grow">
          <SelectInput
            outerBorder={false}
            options={[
              { label: "Direct Entry (Opening Qty)", value: "direct_entry" },
              { label: "By Sheet Type", value: "sheet_section" },
              { label: "By Mark Category", value: "location_category" },
              { label: "By Count Checkbox", value: "count_checkbox" },
            ]}
            value={settings.collection_count_method}
            label={null}
            on:input={(e) => updateCollectionCountMethod(e.detail.value)} />
        </div>
      </div>
      {#if settings.collection_count_method !== "direct_entry"}
        <div class="flex items-center justify-between">
          <div class="flex-none" style="width: 13.25rem;">Document</div>
          <div class="grow">
            <SelectInput
              outerBorder={false}
              label={null}
              options={linkedDocOptions}
              value={settings.collection_count_document_id}
              placeholder="Select Document"
              on:input={(e) => updateSetting("collection_count_document_id", e.detail.value)} />
          </div>
        </div>
        {#if settings.collection_count_method === "sheet_section" && settings.collection_count_document_id !== null}
          <div class="flex items-center justify-between">
            <div class="flex-none" style="width: 13.25rem;">Sheet Type</div>
            <div class="grow">
              <MultiTextInput
                outerBorder={false}
                label={null}
                placeholder="Enter Sheet Type"
                value={settings.collection_count_sheet_section}
                options={collSSOptions}
                on:input={(e) => updateSetting("collection_count_sheet_section", e.detail.value)} />
            </div>
          </div>
        {:else if settings.collection_count_method === "location_category"}
          <div class="flex items-center justify-between">
            <div class="flex-none" style="width: 13.25rem;">Mark Category</div>
            <div class="grow">
              <MultiTextInput
                outerBorder={false}
                placeholder="Enter Mark Category"
                label={null}
                options={locCategoryOptions}
                value={settings.collection_count_location_category}
                on:input={(e) => updateSetting("collection_count_location_category", e.detail.value)} />
            </div>
          </div>
        {/if}
      {/if}
    </div>
    {#if settings.count_method !== "direct_entry"}
      <div class="flex items-center justify-between">
        <div class="flex-none w-56">Multiply Item Counts by Openings</div>
        <div class="grow">
          <BooleanInput
            value={settings.multiply_contained_item_counts}
            label={null}
            on:input={(e) => updateSetting("multiply_contained_item_counts", e.detail.value)} />
        </div>
      </div>
    {/if}
  </div>
{:else if tab === "visibility"}
  <div>
    <div class="flex items-center justify-between">
      <div class="flex-none w-48">Show Orphaned Items</div>
      <div class="grow">
        <BooleanInput
          value={settings.show_untyped_items}
          label={null}
          on:input={(e) => updateSetting("show_untyped_items", e.detail.value)} />
      </div>
    </div>
    <div class="flex items-center justify-between">
      <div class="flex-none w-48">
        Show Unused {settings.group_by === "opening" ? "Openings" : "Types"}
      </div>
      <div class="grow">
        <BooleanInput
          value={settings.show_categories_with_no_items}
          label={null}
          on:input={(e) => updateSetting("show_categories_with_no_items", e.detail.value)} />
      </div>
    </div>
    {#if settings.group_by !== "notes"}
      <div class="flex items-center justify-between">
        <div class="flex-none w-48">Show Item Cutsheets (PDF)</div>
        <div class="grow">
          <BooleanInput
            value={settings.show_item_cut_sheets}
            label={null}
            on:input={(e) => updateSetting("show_item_cut_sheets", e.detail.value)} />
        </div>
      </div>
    {/if}
    {#if settings.group_by === "opening"}
      <div class="flex items-center justify-between">
        <div class="flex-none w-48">Show Opening Cutsheets (PDF)</div>
        <div class="grow">
          <BooleanInput
            value={settings.show_collection_cut_sheets}
            label={null}
            on:input={(e) => updateSetting("show_collection_cut_sheets", e.detail.value)} />
        </div>
      </div>
    {/if}
    <div class="rounded-lg border border-gray-300 p-3 my-2 bg-gray-100 space-y-2">
      <div class="font-bold">Show Totals</div>
      <div class="flex gap-4 flex-wrap">
        <div class="flex gap-1 items-center">
          <BooleanInput
            outerBorder={false}
            minimal
            value={settings.show_total_units}
            label={null}
            on:input={(e) => updateSetting("show_total_units", e.detail.value)} />
          <div class="whitespace-nowrap">Units</div>
        </div>
        <div class="flex gap-1 items-center">
          <BooleanInput
            outerBorder={false}
            minimal
            value={settings.show_total_categories}
            label={null}
            on:input={(e) => updateSetting("show_total_categories", e.detail.value)} />
          <div>{settings.group_by === "opening" ? "Openings" : "Types"}</div>
        </div>
        <div class="flex gap-1 items-center">
          <BooleanInput
            outerBorder={false}
            minimal
            value={settings.show_total_area}
            label={null}
            on:input={(e) => updateSetting("show_total_area", e.detail.value)} />
          <div class="whitespace-nowrap">Item Area</div>
        </div>
        {#if settings.group_by === "opening"}
          <div class="flex gap-1 items-center">
            <BooleanInput
              outerBorder={false}
              minimal
              value={settings.show_total_collection_area}
              label={null}
              on:input={(e) => updateSetting("show_total_collection_area", e.detail.value)} />
            <div class="whitespace-nowrap">Opening Area</div>
          </div>
        {/if}
        <div class="flex gap-1 items-center">
          <BooleanInput
            outerBorder={false}
            minimal
            value={settings.show_total_edge_treatment}
            label={null}
            on:input={(e) => updateSetting("show_total_edge_treatment", e.detail.value)} />
          <div class="whitespace-nowrap">Edge Treatment</div>
        </div>
        <div class="flex gap-1 items-center">
          <BooleanInput
            outerBorder={false}
            minimal
            value={settings.show_total_width}
            label={null}
            on:input={(e) => updateSetting("show_total_width", e.detail.value)} />
          <div class="whitespace-nowrap">Width</div>
        </div>
        <div class="flex gap-1 items-center">
          <BooleanInput
            outerBorder={false}
            minimal
            value={settings.show_total_height}
            label={null}
            on:input={(e) => updateSetting("show_total_height", e.detail.value)} />
          <div class="whitespace-nowrap">Height</div>
        </div>
        <div class="flex gap-1 items-center">
          <BooleanInput
            outerBorder={false}
            minimal
            value={settings.show_total_perimeter}
            label={null}
            on:input={(e) => updateSetting("show_total_perimeter", e.detail.value)} />
          <div>Perimeter</div>
        </div>
      </div>
    </div>
    <HiddenColumns
      hiddenColumns={settings.hidden_columns}
      {standardColumns}
      {customColumns}
      on:toggle-column />
  </div>
{/if}

<style lang="scss">
  .setting-tabs {
    button {
      @apply text-gray-400 border-b border-transparent py-1;

      &.active,
      &:hover {
        @apply text-black;
      }

      &.selected {
        @apply border-black text-black;
      }
    }
  }
</style>
